import { useState } from 'react';
import { useLanguage } from '../i18n/useLanguage';
import VideoModal from '../components/VideoModal';
import { trackStoreClick } from '../lib/analytics';
import { appStoreUrl, googlePlayUrl } from '../lib/storeLinks';

// Localized hero trailer files in /public/videos. The bracketed
// filenames are URL-encoded ([ → %5B, ] → %5D) so they resolve
// correctly as request paths.
const HERO_VIDEOS: Record<string, string> = {
  th: '/videos/%5BTH%5DILC_Teaser_1920x1080.mp4',
  en: '/videos/%5BEN%5DILC_Teaser_1920x1080.mp4',
};

// Single source of truth for "is there a trailer to play yet?".
// Drives both video surfaces in this section: when false the
// desktop click-to-play button shows a centred "Coming Soon"
// label (and its onClick is disabled), and the mobile autoplay
// `<video>` element is replaced by the same "Coming Soon" label
// rendered in front of the dark inner area of the cat-eared frame.
const HAS_VIDEO = true;

export default function HeroSection() {
  const { lang, t } = useLanguage();
  const [videoOpen, setVideoOpen] = useState(false);
  const heroVideoSrc = HERO_VIDEOS[lang] ?? HERO_VIDEOS.th;

  return (
    <section
      id="home"
      className="snap-section relative flex flex-col lg:pt-16"
    >
      {/* "Scene" wrapper — owns the cream background, the decorative
          pattern/fade/characters, and the foreground content. It's the
          only thing that grows with the content column, so the existing
          imagery stays scoped to it instead of stretching across the
          entire taller section. */}
      <div className="relative isolate flex min-h-[calc(100vh-140px)] lg:min-h-[calc(100vh-200px)] flex-col items-center justify-center lg:justify-start overflow-hidden bg-tan pb-12 pt-[60px] md:pt-[70px] lg:justify-center lg:pb-10 lg:pt-[80px]">
        {/* Background pattern overlay (Figma node 1:3514). */}
        <img
          src="/images/hero-bg-pattern.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover"
        />

        {/* Bottom white fade (Figma node 1:3515). */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 -z-4 h-[40%] bg-gradient-to-t from-white via-white/40 to-transparent"
        />

        {/* Side character art (Figma 1:3512 / 1:3513). Reuses the
            shared `animate-float-y` 4s loop (translateY(-10px) +
            rotate(-1.5deg) → back). The right-hand sprite gets a
            negative `animation-delay` of -2s so the two characters
            are roughly 180° out of phase — one is rising while
            the other settles — instead of drifting in lockstep.
            Negative-delay starts the animation partway through
            its cycle on first paint so there's no initial "settle
            in" frame either. */}
        <img
          src="/images/hero-character-right.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-[-28%] lg:left-auto right-auto lg:right-0 top-[16%] lg:top-0 -z-[5] w-[80%] lg:w-[47%] min-h-[50vh] max-h-[95%] object-contain select-none animate-float-y"
          draggable={false}
        />
        <img
          src="/images/hero-character-left.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute right-[-20%] lg:right-auto left-auto lg:left-0 top-[8%] lg:top-0 -z-[5] w-[80%] lg:w-[47%] min-h-[50vh] max-h-[135%] object-contain select-none animate-float-y [animation-delay:-2s]"
          draggable={false}
        />
        

        {/* Foreground content column. Sized to fit within 100vh - 80px (header)
            on standard desktop heights (≥1080p). On shorter screens the column
            overflows naturally and the page scrolls. */}
        <div className="relative z-10 mt-28 lg:mt-0 flex w-full max-w-xl flex-col items-center gap-4 px-4 md:gap-5 md:px-8 lg:gap-4">
          {/* Title logo (the "I LOVE COOKING" mark). */}
          <img
            src="/images/logo.png"
            alt={t('hero.title')}
            className="h-24 w-auto select-none md:h-24 lg:h-[120px]"
            draggable={false}
          />

          {/* Headline — Figma 120:2046. Outer wrapper is the cream
              #F6EADF pill: stretches to the column's width
              (self-stretch), 85.188px tall, fully rounded, with the
              asymmetric Figma padding (17/19/21/22). Inner flex row
              holds [fork-spoon] [title] [fork-spoon]; the icon mask
              trick recolours the cream-tinted fork-spoon asset to
              brand brown #634645 (section-feature) so it stays
              visible on the cream pill and matches the title's
              drop-shadow colour. Title size scales 30 → 36 → 48px
              to land on Figma's 48px at desktop. */}
          

          {/* Decorative chef-hat / order-paper illustration above the video. */}
          {/* <img
            src="/images/hero-chef-decor.svg"
            alt=""
            aria-hidden="true"
            className="pointer-events-none -mb-6 hidden w-[60%] max-w-[380px] select-none md:block lg:-mb-10"
            draggable={false}
          /> */}

          {/* Desktop video button — cat-eared
              frame (cat-video-frame.png). The dark interior
              and pink play triangle are baked into the asset, so
              the button is just a click-target overlaid on the
              PNG; clicking opens the YouTube modal. Hidden below
              lg in favour of the autoplay variant below.
              When `HAS_VIDEO` is false (no trailer ID yet), the
              click handler is suppressed and a centred "Coming
              Soon" label paints in `text-primary` pink over the
              dark interior so the placeholder reads as
              intentional (matches the mobile placeholder below). */}
          <button
            type="button"
            onClick={HAS_VIDEO ? () => setVideoOpen(true) : undefined}
            aria-label={HAS_VIDEO ? t('hero.videoCaption') : t('hero.comingSoon')}
            aria-disabled={HAS_VIDEO ? undefined : 'true'}
            style={{
              backgroundImage: "url('/images/cat-video-frame.png')",
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
            }}
            className={`group relative hidden aspect-[457/312] w-full max-w-[312px] transition-transform duration-200 lg:block ${
              HAS_VIDEO
                ? 'cursor-pointer hover:scale-[1.02]'
                : 'cursor-default'
            }`}
          >
            {!HAS_VIDEO && (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center font-display text-2xl font-extrabold text-primary lg:text-3xl">
                {t('hero.comingSoon')}
              </span>
            )}
          </button>

          {/* Mobile / tablet video — cat-eared frame
              with an autoplaying inline MP4. NOT
              a button: video starts on mount, no tap-to-play
              overlay or modal hand-off. Frame is fixed at 16:9
              regardless of viewport so the proportions match a
              standard widescreen video. The composite is built
              from a single PNG frame (cat-video-frame-mobile.png).
              Inner-video insets come straight from the outer
              SVG's dark rect (16.95, 4.73, 546×307.58 in 580×329.7
              viewBox) and stay valid after the SVG stretches to
              16:9 because they're computed as percentages.
              Width-anchored at 80% of the column (≈80% of screen
              on mobile after column padding). autoPlay + muted +
              playsInline is the iOS/Android-friendly autoplay
              combo; preload="metadata" avoids fetching the full
              clip up front. Drop the trailer file at
              /public/videos/hero-trailer.mp4 for the preview to
              activate; until then the dark inner rect baked into
              the outer SVG shows through. */}
          <div className="mt-2 relative aspect-[13/8.8] w-[80%] max-w-[640px] lg:hidden">
            <img
              src="/images/cat-video-frame-mobile.png"
              alt=""
              aria-hidden="true"
              draggable={false}
              className="pointer-events-none absolute inset-0 h-full w-full select-none z-10"
            />
            {HAS_VIDEO ? (
              <>
                <video
                  key={heroVideoSrc}
                  src={heroVideoSrc}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="absolute bg-[#393939] aspect-[16/9] justify-center items-center w-[95%] h-[85%] select-none object-contain"
                  style={{
                    top: '55%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
                {/* Transparent tap target over the inline preview —
                    opens the full-screen modal player (with sound +
                    controls), mirroring the desktop click-to-play. */}
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  aria-label={t('hero.videoCaption')}
                  className="absolute z-20 aspect-[16/9] w-[95%] h-[85%] cursor-pointer"
                  style={{
                    top: '55%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              </>
            ) : (
              // No trailer URL configured yet — paint a centred
              // "Coming Soon" label in `text-primary` pink over
              // the dark inner area of the cat-eared frame so the
              // placeholder reads as intentional. Same insets as
              // the `<video>` so the label is contained inside
              // the rounded screen rather than overflowing onto
              // the frame border. Mirrors the desktop button's
              // placeholder above.
              <div
                role="status"
                aria-label={t('hero.comingSoon')}
                className="pointer-events-none absolute flex items-center justify-center font-display text-2xl font-extrabold text-primary md:text-3xl"
                style={{
                  top: '1.43%',
                  bottom: '5.27%',
                  left: '2.92%',
                  right: '2.92%',
                }}
              >
                {t('hero.comingSoon')}
              </div>
            )}
          </div>


          <div
            className="flex shrink-0 flex-col items-center justify-center gap-[10px] rounded-[100px] bg-[#F6EADF] px-1 py-2 md:px-2 md:py-4 lg:px-4 lg:py-1"
          >
            <div className="flex items-center justify-center w-full px-4 gap-4 md:gap-4 lg:gap-5">
              <span
                aria-hidden="true"
                className="h-5 w-5 shrink-0 bg-section-feature md:h-5 md:w-5 lg:h-6 lg:w-6"
                style={{
                  WebkitMaskImage: "url('/images/fork-spoon.svg')",
                  maskImage: "url('/images/fork-spoon.svg')",
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                }}
              />
              {/* Visual hero tagline — demoted from <h1> to <p>: the
                  page's single <h1> is the game name (sr-only, in
                  App.tsx) so the primary heading carries the brand, not
                  the CTA copy. */}
              <p className="hero-title-shadow mt-1 mb-2 text-center text-xl font-extrabold leading-none text-white md:text-3xl lg:text-[32px]">
                {t('hero.subtitle')}
              </p>
              <span
                aria-hidden="true"
                className="h-5 w-5 shrink-0 bg-section-feature md:h-5 md:w-5 lg:h-6 lg:w-6"
                style={{
                  WebkitMaskImage: "url('/images/fork-spoon.svg')",
                  maskImage: "url('/images/fork-spoon.svg')",
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                }}
              />
            </div>
          </div>

          {/* Store badges. */}
          <div className="hidden mt-2 lg:flex flex-wrap items-center justify-center gap-4 md:gap-[39px]">
            <a
              href={appStoreUrl(lang)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
              onClick={() => trackStoreClick('apple')}
              className="block transition hover:scale-105 active:scale-95"
            >
              <img
                src="/images/store-app-store.png"
                alt="Download on the App Store"
                className="h-[52px] w-auto"
                draggable={false}
              />
            </a>
            <a
              href={googlePlayUrl(lang)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
              onClick={() => trackStoreClick('google')}
              className="block transition hover:scale-105 active:scale-95"
            >
              <img
                src="/images/store-google-play.png"
                alt="Get it on Google Play"
                className="h-[52px] w-auto"
                draggable={false}
              />
            </a>
          </div>
        </div>

        <VideoModal
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
          videoSrc={heroVideoSrc}
          title={t('hero.videoCaption')}
        />
      </div>

      {/* Extra +200px below the scene. Styled as a white card with a
          rose-100 border and rounded top so it reads as a "tray" sitting
          on the section's accent-lime background — the green only shows
          through the 50px rounded corners at the top. */}
      <div
        aria-hidden="true"
        className="h-[120px] w-full rounded-b-[50px] border-b-[10px] border-rose-100 bg-white shadow-[0_4px_4px_0_rgba(0,0,0,0.25)]"
      />
    </section>
  );
}
